import { DeviceMobile, Laptop, DeviceTablet, Watch, GameController, Package } from "@phosphor-icons/react";

const PHONE_IMG = "/api/static/images/hero-iphone.webp";
const LAPTOP_IMG = "https://images.unsplash.com/photo-1624899195688-81fa68eff47d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwbGFwdG9wJTIwZGVza3xlbnwwfHx8fDE3ODYxMjc0ODB8MA&ixlib=rb-4.1.0&q=85";
const WATCH_IMG = "https://images.unsplash.com/photo-1508685096489-7aacd43bd3b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxzbWFydHdhdGNoJTIwbGlmZXN0eWxlfGVufDB8fHx8MTc4NjEyNzQ4MHww&ixlib=rb-4.1.0&q=85";
const CONSOLE_IMG = "https://images.unsplash.com/photo-1493711662062-fa541adb3fc8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxnYW1pbmclMjBjb25zb2xlJTIwY29udHJvbGxlcnxlbnwwfHx8fDE3ODYxMjc0ODB8MA&ixlib=rb-4.1.0&q=85";
const TABLET_IMG = "https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?crop=entropy&cs=srgb&fm=jpg&q=85";

export const CATEGORIES = [
  {
    slug: "sell-your-phone",
    short: "Phone",
    icon: DeviceMobile,
    title: "Sell Your Phone in Chicago",
    metaTitle: "Sell Your Phone in Chicago — iPhone, Samsung, Pixel",
    metaDescription: "Sell your working iPhone, Samsung, or Google Pixel in Chicago. Transparent in-person evaluation, fast payment. Working devices in good condition only.",
    image: PHONE_IMG,
    imageAlt: "A premium smartphone held in hand, prepared for a buyback evaluation in Chicago",
    intro: "iPhones, Samsung Galaxy, and Google Pixel phones in good working condition hold real value. We explain exactly what yours is worth and why — no inflated promises, no pressure.",
    models: ["iPhone 16 / 15 / 14 series", "iPhone 13 and older", "Samsung Galaxy S & Z series", "Samsung Galaxy A series", "Google Pixel 6 and newer", "Other working smartphones"],
    conditionPoints: [
      "Powers on and holds a charge",
      "Screen, cameras, speakers, and buttons all work",
      "Signed out of iCloud or Google account",
      "No activation or carrier-financing locks",
    ],
    valueFactors: ["Model and release year", "Storage capacity", "Battery health", "Unlocked vs carrier-locked", "Overall physical condition"],
    prepLinks: [
      { to: "/guides/what-to-do-before-selling-your-iphone", label: "What to do before selling your iPhone" },
      { to: "/guides/how-to-factory-reset-samsung-before-selling", label: "Factory reset a Samsung before selling" },
      { to: "/guides/what-determines-resale-value-of-a-phone", label: "What determines resale value" },
    ],
  },
  {
    slug: "sell-your-laptop",
    short: "Laptop",
    icon: Laptop,
    title: "Sell Your Laptop or MacBook in Chicago",
    metaTitle: "Sell Your MacBook or Laptop in Chicago | Sell My Phone Chicago",
    metaDescription: "Sell a working MacBook or Windows laptop in Chicago. Know your model, prep your data, and get a transparent in-person offer. Working devices only.",
    image: LAPTOP_IMG,
    imageAlt: "A MacBook on a clean desk being prepared for resale",
    intro: "MacBooks and quality Windows laptops are among the strongest performers in electronics buyback. Accurate specs and a clean erase are what turn your laptop into a confident sale.",
    models: ["MacBook Pro (all recent generations)", "MacBook Air (M1 and newer)", "Dell XPS and Latitude", "Lenovo ThinkPad", "HP Spectre and EliteBook", "Microsoft Surface laptops"],
    conditionPoints: [
      "Powers on, charges, and runs reliably",
      "Keyboard, trackpad, screen, and ports work",
      "Signed out of iCloud / Microsoft account",
      "Charger included where possible",
    ],
    valueFactors: ["Model, year, and chip", "RAM and storage", "Battery cycle count", "Cosmetic condition", "Included charger and accessories"],
    prepLinks: [
      { to: "/guides/how-to-prepare-a-macbook-before-selling", label: "How to prepare a MacBook before selling" },
      { to: "/guides/how-to-sign-out-of-icloud-before-selling", label: "Sign out of iCloud on your Mac" },
      { to: "/guides/how-electronics-buyback-works-in-chicago", label: "How buyback works in Chicago" },
    ],
  },
  {
    slug: "sell-your-tablet",
    short: "Tablet",
    icon: DeviceTablet,
    title: "Sell Your iPad or Tablet in Chicago",
    metaTitle: "Sell Your iPad or Tablet in Chicago | Sell My Phone Chicago",
    metaDescription: "Sell a working iPad or Android tablet in Chicago. Prepare your device, remove account locks, and get a fair in-person evaluation. Working devices only.",
    image: TABLET_IMG,
    imageAlt: "An iPad-style tablet on a desk, ready for a buyback evaluation",
    intro: "iPads hold their value remarkably well — especially Pro and Air models. Android tablets sell too when they are in good working order and properly reset.",
    models: ["iPad Pro (all sizes)", "iPad Air", "iPad standard and mini", "Samsung Galaxy Tab S series", "Other working tablets"],
    conditionPoints: [
      "Powers on with a healthy battery",
      "Touchscreen and cameras fully working",
      "Signed out of Apple ID or Google account",
      "Cellular models: no carrier balance owed",
    ],
    valueFactors: ["Model and generation", "Storage and cellular option", "Screen condition", "Battery health", "Included accessories like pencils or keyboards"],
    prepLinks: [
      { to: "/guides/how-to-sign-out-of-icloud-before-selling", label: "Sign out of iCloud before selling" },
      { to: "/guides/how-to-remove-google-account-before-selling-android", label: "Remove a Google account" },
      { to: "/guides/how-to-sell-your-phone-safely-in-chicago", label: "Sell safely in Chicago" },
    ],
  },
  {
    slug: "sell-your-smartwatch",
    short: "Smartwatch",
    icon: Watch,
    title: "Sell Your Apple Watch or Smartwatch in Chicago",
    metaTitle: "Sell Your Apple Watch in Chicago | Sell My Phone Chicago",
    metaDescription: "Sell a working Apple Watch or smartwatch in Chicago. Unpair, remove Activation Lock, and get a transparent offer. Working devices only.",
    image: WATCH_IMG,
    imageAlt: "A person wearing an Apple Watch, prepared for resale",
    intro: "Apple Watches move fast in the resale market when they are unpaired and unlocked. A two-minute unpair in the Watch app is the difference between a smooth sale and a declined device.",
    models: ["Apple Watch Ultra", "Apple Watch Series 6 and newer", "Apple Watch SE", "Samsung Galaxy Watch", "Google Pixel Watch", "Garmin and Fitbit wearables"],
    conditionPoints: [
      "Powers on and holds a normal charge",
      "Display, crown, and sensors work",
      "Unpaired from your iPhone (removes Activation Lock)",
      "Band and charger included where possible",
    ],
    valueFactors: ["Series and size", "GPS vs cellular", "Case material", "Battery health", "Included bands and charger"],
    prepLinks: [
      { to: "/guides/what-to-do-before-selling-your-iphone", label: "Unpair before selling your iPhone too" },
      { to: "/guides/how-electronics-buyback-works-in-chicago", label: "How buyback works" },
      { to: "/guides/what-determines-resale-value-of-a-phone", label: "How devices are valued" },
    ],
  },
  {
    slug: "sell-your-gaming-console",
    short: "Console",
    icon: GameController,
    title: "Sell Your Gaming Console in Chicago",
    metaTitle: "Sell Your PS5, Xbox, or Nintendo Console in Chicago",
    metaDescription: "Sell a working PlayStation 5, Xbox Series, or Nintendo Switch in Chicago. Reset it properly, include the cables, and get paid locally. Working devices only.",
    image: CONSOLE_IMG,
    imageAlt: "Gaming controllers beside a console prepared for resale",
    intro: "PS5, Xbox Series, and Nintendo Switch consoles are consistently in demand. A full factory reset and complete cable set put you in the best position at evaluation.",
    models: ["PlayStation 5 (disc & digital)", "PlayStation 4 Pro / Slim", "Xbox Series X / S", "Nintendo Switch & OLED", "Nintendo Switch Lite", "Other working consoles"],
    conditionPoints: [
      "Powers on and runs games reliably",
      "Controllers pair and respond correctly",
      "Account deactivated and console factory reset",
      "Power and HDMI cables included",
    ],
    valueFactors: ["Model and edition", "Disc vs digital", "Storage size", "Controller condition and count", "Included cables and packaging"],
    prepLinks: [
      { to: "/guides/how-to-factory-reset-playstation-before-selling", label: "Factory reset a PlayStation before selling" },
      { to: "/guides/selling-electronics-locally-vs-shipping-online", label: "Local vs shipping your console" },
      { to: "/guides/how-electronics-buyback-works-in-chicago", label: "How buyback works in Chicago" },
    ],
  },
  {
    slug: "sell-sealed-electronics",
    short: "Sealed",
    icon: Package,
    title: "Sell Sealed Electronics in Chicago",
    metaTitle: "Sell Sealed, New-in-Box Electronics in Chicago",
    metaDescription: "Have new, factory-sealed phones, consoles, or laptops? Sealed electronics earn strong offers in Chicago buyback. Proof of purchase may be required.",
    image: PHONE_IMG,
    imageAlt: "A factory-sealed electronics box prepared for sale",
    intro: "Unwanted gifts, carrier upgrades you never opened, duplicate purchases — factory-sealed electronics are the strongest category in buyback, because they resell as new.",
    models: ["Sealed iPhones and Android phones", "Sealed iPads and tablets", "Sealed MacBooks and laptops", "Sealed game consoles", "Sealed smartwatches", "Sealed headphones and accessories"],
    conditionPoints: [
      "Factory seal fully intact — never opened",
      "Proof of purchase available when possible",
      "Not carrier-financed or contract-locked",
      "Model and storage verifiable from the box",
    ],
    valueFactors: ["Model and storage", "Current retail price", "Proof of purchase", "Seal condition", "Market demand"],
    prepLinks: [
      { to: "/guides/how-electronics-buyback-works-in-chicago", label: "How buyback works in Chicago" },
      { to: "/guides/how-to-sell-your-phone-safely-in-chicago", label: "Sell safely in Chicago" },
      { to: "/guides/selling-electronics-locally-vs-shipping-online", label: "Local vs shipping" },
    ],
  },
];
